import { useMemo } from 'react';
import { RouteObject } from 'react-router-dom';

import { useGetUser, useIsHighwaySubscriber } from 'app/api/hooks/core/v1/auth/user';
import { payorRoutes } from 'app/constants/routes';
import { useAuthState, useCanManageUsers } from 'app/state/AuthContext/hooks';
import { isPayorAdmin, isPayorBillingReports, isTriumphInternal } from 'app/utils/lib/user';

import {
  BillingReport,
  Credit,
  CreditLimitReport,
  Credits,
  Dashboard,
  DefaultRedirect,
  FourOhFour,
  GotpFees,
  HighwayAlerts,
  HoldsReport,
  Invoice,
  InvoiceRefundsReport,
  Invoices,
  Load,
  Loads,
  Payment,
  Payments,
  PayorInvoiceReview,
  PayorLayout,
  PreviousSettlements,
  PreviousWeeklySettlements,
  Profile,
  Relationship,
  Relationships,
  RevenueShare,
  Settlement,
  SettlementEdit,
  Settlements,
  SettlementsAcceptTerms,
  SettlementSuccess,
  UnreconciledFundsReport,
  UserCreate,
  UserEdit,
} from './PayorComponents';

export const useGetPayorRoutes = (): RouteObject[] => {
  const { authRoles } = useAuthState();
  const user = useGetUser();

  const canManageUsers = useCanManageUsers();

  const isBillingReports = isPayorBillingReports(user);
  const isAdmin = isPayorAdmin(user);
  const isInternal = isTriumphInternal(authRoles);

  const isHighwaySubscriber = useIsHighwaySubscriber();

  return useMemo(() => [
    {
      children: [
        {
          element: <DefaultRedirect />,
          path: '',
        },
        {
          element: <Dashboard />,
          path: payorRoutes.dashboard,
        },
        ...(isBillingReports
          ? [
            ...(isHighwaySubscriber
              ? [{
                element: <HighwayAlerts />,
                path: payorRoutes.highwayAlerts,
              }]
              : []),
            {
              element: <BillingReport />,
              path: payorRoutes.billingReport,
            },
            {
              element: <CreditLimitReport />,
              path: payorRoutes.creditLimitReport,
            },
            {
              element: <InvoiceRefundsReport />,
              path: payorRoutes.invoiceRefundsReport,
            },
            {
              element: <PayorInvoiceReview />,
              path: payorRoutes.invoiceReview,
            },
            {
              element: <RevenueShare />,
              path: payorRoutes.revenueShare,
            },
            {
              element: <PreviousSettlements />,
              path: payorRoutes.previousSettlements,
            },
            {
              element: <GotpFees />,
              path: payorRoutes.gotpFees,
            },
            {
              element: <PreviousWeeklySettlements />,
              path: payorRoutes.previousWeeklySettlements,
            },
            {
              element: <HoldsReport />,
              path: payorRoutes.holdsReport,
            },
            {
              element: <UnreconciledFundsReport />,
              path: payorRoutes.unreconciledFundsReport,
            },
          ]
          : []),
        ...((isAdmin || isInternal) && canManageUsers
          ? [
            {
              element: <UserCreate />,
              path: `${payorRoutes.users}/new`,
            },
            {
              element: <UserEdit />,
              path: `${payorRoutes.users}/:id/edit`,
            },
          ]
          : []
        ),
        {
          element: <Invoice />,
          path: `${payorRoutes.invoices}/:id`,
        },
        {
          element: <Load />,
          path: `${payorRoutes.loads}/:id`,
        },
        {
          element: <Loads />,
          path: payorRoutes.loads,
        },
        {
          element: <Credit />,
          path: `${payorRoutes.creditNotes}/:id`,
        },
        {
          element: <Credits />,
          path: payorRoutes.creditNotes,
        },
        {
          element: <Invoices />,
          path: payorRoutes.invoices,
        },
        {
          element: <Payment />,
          path: `${payorRoutes.payments}/:id`,
        },
        {
          element: <Payments />,
          path: payorRoutes.payments,
        },
        {
          element: <Profile />,
          path: payorRoutes.profile,
        },
        {
          element: <Relationship />,
          path: `${payorRoutes.relationships}/:id`,
        },
        {
          element: <Relationships />,
          path: payorRoutes.relationships,
        },
        {
          element: <SettlementEdit />,
          path: `${payorRoutes.settlements}/:id/edit`,
        },
        {
          element: <SettlementSuccess />,
          path: `${payorRoutes.settlements}/:id/success`,
        },
        {
          element: <Settlement />,
          path: `${payorRoutes.settlements}/:id`,
        },
        {
          element: <Settlements />,
          path: payorRoutes.settlements,
        },
        {
          element: <SettlementsAcceptTerms />,
          path: payorRoutes.settlementsTerms,
        },
        {
          element: <FourOhFour />,
          path: '*',
        },
      ],
      element: <PayorLayout />,
      path: '/payor',
    },
  ],
  [canManageUsers, isAdmin, isBillingReports, isHighwaySubscriber, isInternal]);
};
